import { useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { REASON_HELP, parseOccurredAt, useAccessFailures } from '../hooks/useAccessFailures';

/** Local time for the admin reading it; the raw value if it cannot be parsed, because
 *  a wrong-looking string beats "Invalid Date" when correlating against Better Stack. */
function formatWhen(value: string): string {
  const d = parseOccurredAt(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

/**
 * The Better Stack view these rows come from. This is the fallback when the API
 * cannot reach Better Stack — the cheaper "just deep-link it" design, kept as the
 * degraded state rather than discarded. An admin is never left with only an error.
 */
const BETTERSTACK_URL = 'https://telemetry.betterstack.com/';

const WINDOWS = [
  { value: '1', label: 'Last hour' },
  { value: '24', label: 'Last 24 hours' },
  { value: '72', label: 'Last 3 days' },
  { value: '168', label: 'Last 7 days' },
];

/** `not_allowlisted` is the ordinary case — someone who has not been granted access
 *  yet. `no_amr` appearing for everyone at once means GoTrue stopped emitting the
 *  claim and every admin is locked out, which is the one reason worth alarming on. */
const ALARMING = new Set(['no_amr']);

export function AccessFailures() {
  const [hours, setHours] = useState('24');
  const query = useAccessFailures(Number(hours));
  const rows = query.data?.rows ?? [];

  const unconfigured = query.error?.code === 'betterstack_unconfigured';
  const misconfigured = query.error?.code === 'betterstack_misconfigured';
  const unavailable = query.error?.code === 'betterstack_unavailable';

  return (
    <Stack>
      <Text size="sm" c="dimmed">
        Admin sign-in refusals, read live from Better Stack. Nothing is stored here —
        Better Stack remains the record. This answers “why was this person refused”,
        not “why can a member not sign in”.
      </Text>

      <Group align="end">
        <Select
          label="Window"
          data={WINDOWS}
          value={hours}
          onChange={(v) => setHours(v ?? '24')}
          w={200}
          allowDeselect={false}
        />
        <Button variant="default" onClick={() => query.refetch()} loading={query.isFetching}>
          Refresh
        </Button>
      </Group>

      {unconfigured && (
        <Alert color="yellow" title="Better Stack is not connected in this environment">
          <Text size="sm">
            The query credentials are not set, so this screen has nothing to read. The logs
            still exist — open{' '}
            <Anchor href={BETTERSTACK_URL} target="_blank" rel="noreferrer">
              Better Stack
            </Anchor>{' '}
            and search for <code>admin gate rejected</code>.
          </Text>
        </Alert>
      )}

      {misconfigured && (
        <Alert color="orange" title="Better Stack connection is misconfigured">
          <Text size="sm">
            {query.error?.message}. Every credential is present, so fix that one value in the
            API environment rather than re-creating the connection. Until then, open{' '}
            <Anchor href={BETTERSTACK_URL} target="_blank" rel="noreferrer">
              Better Stack
            </Anchor>{' '}
            and search for <code>admin gate rejected</code>.
          </Text>
        </Alert>
      )}

      {unavailable && (
        <Alert color="red" title="Better Stack did not answer">
          <Text size="sm">
            {query.error?.message} The logs are unaffected — open{' '}
            <Anchor href={BETTERSTACK_URL} target="_blank" rel="noreferrer">
              Better Stack
            </Anchor>{' '}
            directly and search for <code>admin gate rejected</code>.
          </Text>
        </Alert>
      )}

      {query.isError && !unconfigured && !misconfigured && !unavailable && (
        <Alert color="red" title="Couldn't load access failures">
          <Text size="sm">{query.error?.message}</Text>
        </Alert>
      )}

      {query.isPending && <Loader size="sm" />}

      {!query.isPending && !query.isError && rows.length === 0 && (
        <Text size="sm" c="dimmed">
          No refusals in this window. Nobody was turned away.
        </Text>
      )}

      {rows.length > 0 && (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>When</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Reason</Table.Th>
              <Table.Th>Path</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row, i) => (
              <Table.Tr key={`${row.occurred_at}-${row.actor_email}-${i}`}>
                <Table.Td>{formatWhen(row.occurred_at)}</Table.Td>
                <Table.Td>{row.actor_email || '—'}</Table.Td>
                <Table.Td>
                  <Tooltip
                    label={REASON_HELP[row.reason] ?? 'Unrecognised reason.'}
                    multiline
                    w={320}
                    withArrow
                  >
                    <Badge color={ALARMING.has(row.reason) ? 'red' : 'gray'} variant="light">
                      {row.reason || 'unknown'}
                    </Badge>
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {row.url || '—'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}
